import { CALENDAR_DRAG_TYPE, isSlotDroppableId } from './dragTypes';

/**
 * Which meal slot a drag is released over, read from the pointer rather than from
 * react-beautiful-dnd.
 *
 * rbd picks a destination by overlap: every droppable the dragged item's box touches is a
 * candidate, and of several it takes the one furthest from where the drag began. That suits a list
 * of rows. It does not suit a grid of slots 16px tall, which a meal chip (20px) or a grocery row
 * (36px by 400px) covers several of at once -- drops landed a slot, or a whole day, away from the
 * pointer. So while a drag is in flight this follows the pointer, marks the slot under it with
 * `data-pointer-over` for the CSS to tint, and at the end says which slot that was. rbd still moves
 * the item, and still decides everything that is not a meal slot.
 *
 * A keyboard drag never moves the pointer, so nothing is tracked and rbd's destination stands.
 */

export const POINTER_OVER_ATTRIBUTE = 'data-pointer-over';

let view = null;
let tracked = false;
let lastPoint = null;
let droppableId = null;
let highlighted = null;

/** The meal slot's droppable id at a viewport point, or null when the point is not on a slot. */
export const slotDroppableIdAt = (doc, x, y) => {
	const element = typeof doc.elementFromPoint === 'function' ? doc.elementFromPoint(x, y) : null;
	const slot = element && element.closest ? element.closest('.meal-slot') : null;
	const droppable = slot ? slot.querySelector('[data-rbd-droppable-id]') : null;
	const id = droppable ? droppable.getAttribute('data-rbd-droppable-id') : null;
	return isSlotDroppableId(id) ? id : null;
};

const highlight = (slot) => {
	if(highlighted === slot) {
		return;
	}
	if(highlighted) {
		highlighted.removeAttribute(POINTER_OVER_ATTRIBUTE);
	}
	highlighted = slot;
	if(slot) {
		slot.setAttribute(POINTER_OVER_ATTRIBUTE, '');
	}
};

const updateTarget = () => {
	const doc = view.document;
	droppableId = slotDroppableIdAt(doc, lastPoint.x, lastPoint.y);
	const droppable = droppableId ? doc.querySelector(`[data-rbd-droppable-id="${droppableId}"]`) : null;
	highlight(droppable ? droppable.closest('.meal-slot') : null);
};

const onPointerMove = (event) => {
	const point = event.touches && event.touches.length ? event.touches[0] : event;
	if(typeof point.clientX !== 'number') {
		return;
	}
	tracked = true;
	lastPoint = { x: point.clientX, y: point.clientY };
	updateTarget();
};

// rbd scrolls the page when a drag nears the edge of the window, with the pointer held still:
// the slot under it changes without a mousemove.
const onScroll = () => {
	if(lastPoint) {
		updateTarget();
	}
};

/** Called as a drag is about to start. */
export const startPointerTracking = (target = window) => {
	stopPointerTracking();
	view = target;
	view.addEventListener('mousemove', onPointerMove, true);
	view.addEventListener('touchmove', onPointerMove, { capture: true, passive: true });
	view.addEventListener('scroll', onScroll, { capture: true, passive: true });
};

/**
 * Called as the drag ends. `tracked` is false when the pointer never moved -- a keyboard drag --
 * and `droppableId` is the slot under the pointer's last position, or null.
 */
export const stopPointerTracking = () => {
	const result = { tracked, droppableId };
	if(view) {
		view.removeEventListener('mousemove', onPointerMove, true);
		view.removeEventListener('touchmove', onPointerMove, { capture: true, passive: true });
		view.removeEventListener('scroll', onScroll, { capture: true, passive: true });
	}
	highlight(null);
	view = null;
	tracked = false;
	lastPoint = null;
	droppableId = null;
	return result;
};

/**
 * rbd's drop result, with its destination replaced by the slot the pointer was released over.
 *
 * Released over a slot rbd did not pick, the meal goes to the end of that slot. Released anywhere
 * that is not a slot, a destination rbd put in a slot is dropped, since the user was not pointing
 * at it; a grocery list reorder is left alone.
 */
export const resolveCalendarDestination = (result, pointer) => {
	if(!result || result.type !== CALENDAR_DRAG_TYPE || !pointer || !pointer.tracked) {
		return result;
	}
	const destination = result.destination;
	if(pointer.droppableId) {
		if(destination && destination.droppableId === pointer.droppableId) {
			return result;
		}
		return { ...result, destination: { droppableId: pointer.droppableId, index: Infinity } };
	}
	if(destination && isSlotDroppableId(destination.droppableId)) {
		return { ...result, destination: null };
	}
	return result;
};

/**
 * A Draggable's style, with rbd's drop animation skipped when it is released over a meal slot.
 *
 * rbd animates a dropped item into place for a third of a second or more and only then calls
 * onDragEnd, and it animates it toward its own idea of the destination, which for a slot is not
 * the one the pointer is over. The drop felt like a pause, then a jump. Over a slot the item now
 * lands at once. The duration cannot be 0: rbd finishes the drop on the transitionend event, and
 * a transition of 0s never fires one. Anywhere else, such as a list reorder, rbd's animation
 * goes where the item is going and is kept.
 */
export const calendarDropStyle = (style, snapshot) => {
	if(!snapshot.isDropAnimating || !droppableId) {
		return style;
	}
	return { ...style, transitionDuration: '0.001s' };
};
